<template>
    <div class="md:ppv2-w-[80%] ppv2-w-full">
        <form @submit.prevent="save">
            <!-- TODO: Uncomment when InfoBanner.vue is available -->
            <!-- <InfoBanner :text="$t('website_settings.seo.info_banner')" class="ppv2-mb-6" /> -->

            <!-- SEO sections for each page -->
            <div v-if="localSeos.length > 0" class="ppv2-flex ppv2-flex-col ppv2-gap-4">
                <SectionCard
                    v-for="section in localSeos"
                    :key="section.id"
                    :title="section.title"
                    :initiallyExpanded="expandedSections[section.id]"
                    :collapsible="true"
                    @toggle="toggleSection(section.id, $event)"
                >
                    <!-- Header Left: URL -->
                    <template #header-right>
                        <div class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-ml-2">
                            <Link :size="16" class="ppv2-text-gray-400" />
                            <a
                                :href="getSectionUrl(section.section_url)"
                                target="_blank"
                                class="ppv2-text-sm ppv2-text-gray-400 ppv2-underline hover:ppv2-text-primary-500 ppv2-transition-colors poppinsLight"
                                @click.stop
                            >
                                {{ getSectionUrl(section.section_url) }}
                            </a>
                        </div>
                    </template>

                    <!-- Header Right: Status Badge & Chevron -->
                    <template #header-actions>
                        <div class="ppv2-flex ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                            <!-- Status Badge -->
                            <div
                                v-if="isConfigured(section)"
                                class="ppv2-px-3 ppv2-py-1 ppv2-bg-emerald-50 ppv2-text-emerald-600 ppv2-text-sm ppv2-rounded-full poppinsMedium"
                            >
                                {{ $t('website_settings.seo.configured_badge') }}
                            </div>
                            <div
                                v-else
                                class="ppv2-px-3 ppv2-py-1 ppv2-bg-[#FEF2F2] ppv2-text-[#EF4444] ppv2-text-sm ppv2-rounded-full poppinsMedium"
                            >
                                {{ $t('website_settings.seo.to_configure_badge') }}
                            </div>
                        </div>
                    </template>

                    <!-- Content -->
                    <div class="ppv2-grid ppv2-grid-cols-1 ppv2-gap-6 ppv2-pt-2">
                        <!-- SEO Title -->
                        <div>
                            <CustomInput
                                v-model="section.titre"
                                :label="$t('website_settings.seo.title_label')"
                                :maxlength="60"
                                :placeholder="$t('website_settings.seo.title_placeholder')"
                                customLabelClass="!poppinsLight ppv2-text-black"
                                classContainerAdd="!ppv2-mb-1"
                                :errorText="errors['titre' + section.id]"
                            />
                            <div class="ppv2-flex ppv2-justify-end">
                                <span class="ppv2-text-xs ppv2-text-gray-400 poppinsLight">
                                    {{ $t('website_settings.seo.char_max', { max: 60 }) }} - {{ (section.titre || '').length }}/60
                                </span>
                            </div>
                        </div>

                        <!-- Meta Description -->
                        <div>
                            <BaseTextarea
                                v-model="section.description"
                                :label="$t('website_settings.seo.description_label')"
                                :use-editor="false"
                                :rows="4"
                                :maxlength="160"
                                :placeholder="$t('website_settings.seo.description_placeholder')"
                                customLabelClass="!poppinsLight ppv2-text-black"
                                classContainerAdd="!ppv2-mb-1"
                                :error="
                                    errors['description' + section.id]
                                        ? errors['description' + section.id][0]
                                        : errors['description' + section.id]
                                "
                                :state="errors['description' + section.id] ? 'error' : 'default'"
                            />
                            <div class="ppv2-flex ppv2-justify-end">
                                <span class="ppv2-text-xs ppv2-text-gray-400 poppinsLight">
                                    {{ $t('website_settings.seo.char_max', { max: 160 }) }} -
                                    {{ (section.description || '').length }}/160
                                </span>
                            </div>
                        </div>

                        <!-- Social Image -->
                        <div>
                            <CustomInputUpload
                                :label="$t('website_settings.seo.image_label')"
                                customClassLabel="poppinsLight ppv2-text-black"
                                v-model="section.image_seo"
                                @input="handleImageUpload(section.id, $event)"
                                :specificTaille="$t('website_settings.seo.image_size_recommendation')"
                                classContainerAdd="!ppv2-mb-0"
                            />
                        </div>
                    </div>
                </SectionCard>
            </div>

            <!-- Empty State -->
            <div v-else class="ppv2-text-center ppv2-py-12 ppv2-text-gray-500">
                <Search :size="48" class="ppv2-mb-4 ppv2-opacity-30 ppv2-inline-block" />
                <p>{{ $t('website_settings.seo.empty_state') }}</p>
            </div>
        </form>
    </div>
</template>

<script>
import CustomInput from '../../../../Inputs/CustomInput.vue'
import SectionCard from './SectionCard.vue'
import BaseTextarea from '../../../../Shared/BaseTextarea.vue'
import BaseButton from '../../../../Shared/BaseButton.vue'
// TODO: Uncomment when InfoBanner.vue is available
// import InfoBanner from '../../../../Shared/InfoBanner.vue'
import BaseLabel from '../../../../Shared/BaseLabel.vue'
import CustomInputUpload from '../../../../Inputs/CustomInputUpload.vue'
import { ChevronDown, ChevronRight, Link, Image as ImageIcon, Search } from 'lucide-vue'
import normalizeSize from '../../../../../utils/normalizeSize'

export default {
    name: 'TabSeo',
    components: {
        SectionCard,
        CustomInput,
        BaseTextarea,
        BaseButton,
        // TODO: Uncomment when InfoBanner.vue is available
        // InfoBanner,
        BaseLabel,
        CustomInputUpload,
        ChevronDown,
        ChevronRight,
        Link,
        ImageIcon,
        Search,
    },
    props: {
        data: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            saving: false,
            localSeos: [],
            expandedSections: {},
            errors: {},
        }
    },
    computed: {
        baseFrontUrl() {
            let url = this.data?.domain || window.location.origin
            return url.split('/back-office')[0].replace('-bo.', '.')
        },
    },
    mounted() {
        this.initializeForm()
    },
    activated() {
        this.$root.$off('website-settings-trigger-save-seo', this.save)
        this.$root.$on('website-settings-trigger-save-seo', this.save)
    },
    deactivated() {
        this.$root.$off('website-settings-trigger-save-seo', this.save)
    },
    beforeDestroy() {
        this.$root.$off('website-settings-trigger-save-seo', this.save)
    },
    methods: {
        normalizeSize,
        getSectionUrl(sectionUrl) {
            const path = (sectionUrl || '').startsWith('/') ? sectionUrl : '/' + (sectionUrl || '')
            return this.baseFrontUrl + path
        },
        initializeForm() {
            // Get SEO sections from API data
            const seos = this.data?.seos || []
            this.localSeos = seos.map((s) => ({
                id: s.id,
                title: s.title || '',
                section_url: s.section_url || '',
                titre: s.seo_title || s.title || '',
                description: s.description || '',
                image_seo: s.image_seo || null,
                image_seo_file: null,
                delete_image_seo_file: false,
            }))

            // Expand first section by default
            if (this.localSeos.length > 0) {
                this.$set(this.expandedSections, this.localSeos[0].id, true)
            }
        },
        toggleSection(id, state) {
            this.$set(this.expandedSections, id, state !== undefined ? state : !this.expandedSections[id])
        },
        isConfigured(section) {
            return !!(section.titre && section.description)
        },
        handleImageUpload(id, file) {
            const section = this.localSeos.find((s) => s.id === id)
            if (section) {
                if (file) {
                    const reader = new FileReader()
                    reader.onload = (e) => {
                        section.image_seo = e.target.result
                    }
                    reader.readAsDataURL(file)
                    section.image_seo_file = file
                    section.delete_image_seo_file = false
                } else {
                    section.image_seo = null
                    section.image_seo_file = null
                    section.delete_image_seo_file = true
                }
            }
        },
        async save() {
            this.errors = {}
            this.saving = true
            this.$emit('save', { loading: true })
            try {
                const formData = new FormData()

                for (const section of this.localSeos) {
                    formData.append('titre' + section.id, section.titre || '')
                    formData.append('description' + section.id, section.description || '')

                    if (section.image_seo_file) {
                        formData.append('image_seo' + section.id, section.image_seo_file)
                    }
                    if (section.delete_image_seo_file) {
                        formData.append('delete_image_seo' + section.id, section.delete_image_seo_file)
                    }
                }

                const response = await axios.post('/back-office/website/apropos-settings/seo-setting', formData)
                this.$emit('save', { loading: false, success: true, message: response.data?.message })
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.$emit('save', { loading: false })
                if (error.response && error.response.status === 422) {
                    this.errors = error.response.data.errors

                    // Identify sections with errors
                    const sectionIdsWithErrors = new Set()
                    Object.keys(this.errors).forEach((key) => {
                        const idMatch = key.match(/\d+$/)
                        if (idMatch) {
                            sectionIdsWithErrors.add(parseInt(idMatch[0]))
                        }
                    })

                    // Handle expansion: expand if error, collapse if not
                    this.localSeos.forEach((section) => {
                        this.$set(this.expandedSections, section.id, sectionIdsWithErrors.has(section.id))
                    })
                }
            } finally {
                this.saving = false
            }
        },
    },
}
</script>
